import type { OrderMarginType } from '@flipster/utils/types/base';

import { TpSlDragIntent } from './tp-sl-drag-intent';

/**
 * 기존 TP/SL 라인을 드롭했을 때 "직접 수정" vs "모달 오픈"을 가르는 순수 판정.
 *
 * 주문 타입(`lineType`)과 드롭된 구간(`draggedZoneKind` = 진입가 기준 이익이면 'TP'/손실이면 'SL')이
 * 어긋나면 — 즉 **TP를 손실 구간** 또는 **SL을 이익 구간** 으로 드롭하면 — 직접 수정 대신 모달을
 * 띄운다. 같은 구간이면(TP→이익 / SL→손실) 직접 수정한다.
 *
 * - TP + 이익구간 → false (직접 수정)
 * - TP + 손실구간 → true  (모달)
 * - SL + 손실구간 → false (직접 수정)
 * - SL + 이익구간 → true  (모달)
 */
export function isTpSlZoneMismatch(lineType: 'TP' | 'SL', draggedZoneKind: TpSlDragIntent['kind']): boolean {
  return draggedZoneKind !== lineType;
}

/**
 * SL 라인을 **청산가 너머**로 드롭했는지 — 직접 re-price(서버 거부) 대신 모달을 띄워 liq 에러를
 * 보여주고 Confirm을 막을 케이스. 모달의 `evaluatePriceCase` liq 판정과 **동일 기준**으로 맞춘다:
 * Isolated + liq>0 + (Long: price≤liq / Short: price≥liq). TP나 Cross엔 해당 없음(false).
 */
export function isTpSlLiqCross(params: {
  lineType: 'TP' | 'SL';
  positionSide: 'Long' | 'Short';
  price: number;
  liqPrice: number;
  marginType: OrderMarginType | undefined;
}): boolean {
  const { lineType, positionSide, price, liqPrice, marginType } = params;
  if (lineType !== 'SL') return false;
  if (marginType !== 'Isolated') return false;
  if (!(liqPrice > 0)) return false;
  return positionSide === 'Long' ? price <= liqPrice : price >= liqPrice;
}

/**
 * TP/SL 라인 ×(삭제) 핸들러를 결정한다.
 *
 * - webview: web 확인 다이얼로그 없이 `onCancelClick`을 곧바로 호출 → DELETE_TPSL 브리지 즉시 전송
 *   (확인은 native 몫, ERD Flow D).
 * - 데스크톱: `confirm(tpslType, doRemove)`로 web 확인 다이얼로그를 띄우고, Confirm일 때만 삭제.
 */
export function resolveTpSlCancelClick(params: {
  isWebview: boolean;
  tpslType: 'tp' | 'sl';
  onCancelClick: (orderId: string) => Promise<void>;
  confirm: (tpslType: 'tp' | 'sl', doRemove: () => Promise<void>) => Promise<void>;
}): (orderId: string) => Promise<void> {
  const { isWebview, tpslType, onCancelClick, confirm } = params;
  if (isWebview) return onCancelClick;
  return (orderId) => confirm(tpslType, () => onCancelClick(orderId));
}
